import React from 'react';
import { Clock } from 'lucide-react';
import { Button, ButtonSize, ButtonVariant } from '../ui';
import { CreditRequestState } from '../../hooks/useCreditRequest';
import { REQUEST_CREDITS_LABEL, declinedNotice, pendingNotice } from '../../utils/creditRequest';

/**
 * The one control for "Request more credits": the button while nothing is
 * open, and — instead of the button — the notice "Requested on 3 Oct, waiting
 * for a platform admin" while one is, or "Our team declined this on
 * 3 Oct. You can ask again after 4 Oct, 14:30." while the wait after a decline
 * runs. Never a button that does nothing: a request already open, or a wait
 * that has not ended, is a fact to read, not an action to offer.
 *
 * Presentational. The state comes from `useCreditRequest`, so the picker, the
 * refused card and the balance popover can all hold the same one.
 */
export interface CreditRequestTriggerProps {
  state: Pick<CreditRequestState, 'phase' | 'requestedOn' | 'declinedOn' | 'askAgainAt'>;
  onRequest: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Another action on the surface is running (e.g. discovery is starting). */
  disabled?: boolean;
  className?: string;
}

const spinner = (
  <span className="h-[13px] w-[13px] animate-spin rounded-round border-2 border-current border-t-transparent opacity-70" />
);

export const CreditRequestTrigger: React.FC<CreditRequestTriggerProps> = ({
  state, onRequest, variant = 'secondary', size = 'md', disabled = false, className = '',
}) => {
  const { phase, requestedOn, declinedOn, askAgainAt } = state;

  // Just sent: the outcome line says so, and the notice would only repeat it.
  if (phase === 'requested') return null;

  if (phase === 'pending' && requestedOn) {
    return (
      <p role="status" className={`m-0 flex items-center gap-s2 text-body-sm text-ink-secondary ${className}`}>
        <Clock className="h-[13px] w-[13px] shrink-0" aria-hidden="true" />
        {pendingNotice(requestedOn)}
      </p>
    );
  }

  if (phase === 'declined' && declinedOn && askAgainAt) {
    return (
      <p role="status" className={`m-0 flex items-start gap-s2 text-body-sm text-ink-secondary ${className}`}>
        <Clock className="mt-[2px] h-[13px] w-[13px] shrink-0" aria-hidden="true" />
        {declinedNotice(declinedOn, askAgainAt)}
      </p>
    );
  }

  const requesting = phase === 'requesting';
  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={onRequest}
      disabled={disabled || requesting || phase === 'loading'}
      iconLeft={requesting ? spinner : undefined}
    >
      {requesting ? 'Requesting…' : REQUEST_CREDITS_LABEL}
    </Button>
  );
};

export default CreditRequestTrigger;
